import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import { useAuth } from '../../components/context/AuthContext';
import { db } from '../../firebase';
import { collection, query, where, getDocs, getDoc, doc, updateDoc, Timestamp, orderBy, limit, startAfter } from 'firebase/firestore';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import { Container, Button, Row, Col, Alert, Spinner, ListGroup, Card, Badge } from 'react-bootstrap';
import { createLogger } from '../../utils/logger';

const logger = createLogger('ManagePackedOrdersPage');

export default function ManagePackedOrdersPage() {
  const { currentUser, userRole, employeeSupermarketId } = useAuth();
  const navigate = useNavigate();
  const [packedOrders, setPackedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [showFinancialData, setShowFinancialData] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const lastDocRef = useRef(null);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Summary statistics state
  const [summaryStats, setSummaryStats] = useState({
    totalPackedAwaitingPickup: 0,
    totalCompleted: 0,
    totalCancelled: 0,
    fullPaymentCount: 0,
    confirmationFeeOnlyCount: 0,
    confirmationFeeTotal: 0,
    fullPaymentTotal: 0,
    fullPaymentMinusConfirmationFee: 0,
    totalOrders: 0,
    deliveryOrders: 0,
    pickupOrders: 0
  });

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { path: "/employee/dashboard", label: "Employee Hub" },
    { label: "Manage Packed Orders" }
  ];

  // Helper function to determine if an order is a full payment
  const isFullPaymentOrder = useCallback((order) => {
    if (order.paymentType === 'full') {
      return true;
    }
    
    const orderSubtotal = parseFloat(order.orderSubtotal) || 0;
    const confirmationFeeAmount = parseFloat(order.confirmationFee) || parseFloat(process.env.REACT_APP_CONFIRMATION_FEE) || 500;
    const deliveryFeeAmount = order.deliveryOption === 'delivery' ? (Number(order.deliveryFee) || 0) : 0;
    const expectedFullTotal = orderSubtotal + confirmationFeeAmount + deliveryFeeAmount;
    const amountPaid = parseFloat(order.amountPaid) || parseFloat(order.total) || 0;
    
    if (orderSubtotal > 0 && Math.abs(amountPaid - expectedFullTotal) < 0.01) {
      return true;
    }
    
    return false;
  }, []);

  // Fetch summary statistics for packed orders
  const fetchSummaryStats = useCallback(async () => {
    try {
      const confirmationFee = parseFloat(process.env.REACT_APP_CONFIRMATION_FEE) || 500;
      
      // Query for all packed orders
      let allOrdersQuery = query(
        collection(db, 'Transactions'),
        where('status', 'in', ['packed_awaiting_pickup', 'completed', 'cancelled_by_employee']),
        orderBy('createdAt', 'desc')
      );
      
      if (userRole === 'Employee' && employeeSupermarketId) {
        allOrdersQuery = query(allOrdersQuery, where('supermarketId', '==', employeeSupermarketId));
      }
      
      const allSnapshot = await getDocs(allOrdersQuery);
      const allOrders = allSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Calculate statistics
      const stats = {
        totalPackedAwaitingPickup: 0,
        totalCompleted: 0,
        totalCancelled: 0,
        fullPaymentCount: 0,
        confirmationFeeOnlyCount: 0,
        confirmationFeeTotal: 0,
        fullPaymentTotal: 0,
        fullPaymentMinusConfirmationFee: 0,
        totalOrders: allOrders.length,
        deliveryOrders: 0,
        pickupOrders: 0
      };
      
      allOrders.forEach(order => {
        // Count by status
        if (order.status === 'packed_awaiting_pickup') stats.totalPackedAwaitingPickup++;
        if (order.status === 'completed') stats.totalCompleted++;
        if (order.status === 'cancelled_by_employee') stats.totalCancelled++;
        
        // Count delivery vs pickup
        if (order.deliveryOption === 'delivery') {
          stats.deliveryOrders++;
        } else {
          stats.pickupOrders++;
        }
        
        // Exclude cancelled and active orders for financial calculations
        const isActive = order.status !== 'cancelled_by_employee' && order.status !== 'active';
        
        if (isActive) {
          const isFullPayment = isFullPaymentOrder(order);
          
          if (isFullPayment) {
            stats.fullPaymentCount++;
            const orderSubtotal = parseFloat(order.orderSubtotal) || 0;
            const confirmationFeeAmount = parseFloat(order.confirmationFee) || confirmationFee;
            stats.fullPaymentTotal += orderSubtotal + confirmationFeeAmount;
            stats.fullPaymentMinusConfirmationFee += orderSubtotal;
            stats.confirmationFeeTotal += confirmationFeeAmount; // Include fee portion
          } else {
            stats.confirmationFeeOnlyCount++;
            stats.confirmationFeeTotal += confirmationFee; // Only confirmation fee
          }
        }
      });
      
      setSummaryStats(stats);
    } catch (err) {
      logger.error('[ManagePackedOrdersPage] Error fetching summary stats:', err);
    }
  }, [userRole, employeeSupermarketId, isFullPaymentOrder]);

  // Helper function to format dates nicely
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Handle Firestore Timestamp
    if (timestamp && typeof timestamp.toDate === 'function') {
      const date = timestamp.toDate();
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);
      
      if (diffInHours < 24) {
        // Today - show time only
        return date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        });
      } else if (diffInHours < 48) {
        // Yesterday
        return `Yesterday at ${date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        })}`;
      } else {
        // Older - show date and time
        return `${date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: 'numeric'
        })  } at ${  date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        })}`;
      }
    }
    
    // Handle regular Date object
    if (timestamp instanceof Date) {
      const now = new Date();
      const diffInHours = (now - timestamp) / (1000 * 60 * 60);
      
      if (diffInHours < 24) {
        // Today - show time only
        return timestamp.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        });
      } else if (diffInHours < 48) {
        // Yesterday
        return `Yesterday at ${timestamp.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        })}`;
      } else {
        // Older - show date and time
        return `${timestamp.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: 'numeric'
        })  } at ${  timestamp.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        })}`;
      }
    }
    
    // Fallback for other types
    return 'Invalid Date';
  };

  const fetchUserData = async (userId) => {
    if (!userId) return 'N/A';
    
    try {
      // First try querying by identity_id (this is what's stored in transactions)
      const userQuery = query(collection(db, 'Users'), where("identity_id", "==", userId));
      const querySnapshot = await getDocs(userQuery);
      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        return userData.first_name || userData.displayName || userData.email || 'Unknown User';
      }
      
      // If not found by identity_id, try to get user data directly by document ID
      const userDocRef = doc(db, 'Users', userId);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.first_name || userData.displayName || userData.email || 'Unknown User';
      }
      
      // If still not found, return a more descriptive message
      return `User ${userId.substring(0, 8)}... (Not found)`;
      
    } catch (err) {
      logger.error("[ManagePackedOrdersPage] Error fetching user data:", err);
      return 'Error Fetching Name';
    }
  };

  // Function to determine who confirmed the order
  const getConfirmationInfo = (order) => {
    if (order.status !== 'completed') return null;
    
    // Check if order was confirmed by employee (has pickedUpAt timestamp)
    if (order.pickedUpAt) {
      return {
        type: 'employee',
        timestamp: order.pickedUpAt,
        text: order.deliveryOption === 'delivery' ? 'Delivery Confirmed by Employee' : 'Pickup Confirmed by Employee'
      };
    }
    
    // Check if order was confirmed by user (has completedAt but no pickedUpAt)
    // This happens when user confirms on Carts.js page
    if (order.completedAt && !order.pickedUpAt) {
      return {
        type: 'user',
        timestamp: order.completedAt,
        text: order.deliveryOption === 'delivery' ? 'Delivery Confirmed by Customer' : 'Pickup Confirmed by Customer'
      };
    }
    
    // Additional check: if the order has a cartInstanceId and we can determine it was user-confirmed
    // This handles cases where the transaction might not have completedAt but the cart instance is completed
    if (order.cartInstanceId) {
      // If the order is completed but has no timestamps, it's likely user-confirmed
      return {
        type: 'user',
        timestamp: order.updatedAt || order.createdAt,
        text: order.deliveryOption === 'delivery' ? 'Delivery Confirmed by Customer' : 'Pickup Confirmed by Customer'
      };
    }
    
    return null;
  };

  const fetchPackedOrders = useCallback(async (isLoadMore = false, isRefresh = false) => {
    if (!currentUser) {
      setError("Please log in to manage packed orders."); 
      setLoading(false); 
      return;
    }
    if (userRole !== 'Admin' && userRole !== 'Employee') {
      setError("You are not authorized to view this page. Requires Admin or Employee role."); 
      setLoading(false); 
      return;
    }
    
    if (isRefresh) {
      setRefreshing(true);
    } else if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setCurrentPage(1);
      setLastDoc(null);
      lastDocRef.current = null;
    }
    
    setError(null);
    
    try {
      // First, try to get orders with packedAt timestamp
      let ordersQuery = query(
        collection(db, 'Transactions'),
        where('status', 'in', ['packed_awaiting_pickup', 'completed']),
        orderBy('packedAt', 'desc'),
        limit(ordersPerPage)
      );
      
      if (userRole === 'Employee' && employeeSupermarketId) {
        ordersQuery = query(ordersQuery, where('supermarketId', '==', employeeSupermarketId));
      } else if (userRole === 'Employee' && !employeeSupermarketId) {
        setPackedOrders([]); 
        setLoading(false); 
        return;
      }
      
      // Add pagination - use ref to avoid useEffect re-running when lastDoc changes
      const cursorDoc = isLoadMore ? lastDocRef.current : null;
      if (isLoadMore && cursorDoc) {
        ordersQuery = query(ordersQuery, startAfter(cursorDoc));
      }
      
      let querySnapshot;
      try {
        querySnapshot = await getDocs(ordersQuery);
      } catch (orderByError) {
        // If orderBy fails (no packedAt field), try without orderBy
        logger.warn("OrderBy packedAt failed, trying without orderBy:", orderByError);
        ordersQuery = query(
          collection(db, 'Transactions'),
          where('status', 'in', ['packed_awaiting_pickup', 'completed']),
          limit(ordersPerPage)
        );
        
        if (userRole === 'Employee' && employeeSupermarketId) {
          ordersQuery = query(ordersQuery, where('supermarketId', '==', employeeSupermarketId));
        }
        
        if (isLoadMore && lastDocRef.current) {
          ordersQuery = query(ordersQuery, startAfter(lastDocRef.current));
        }
        
        querySnapshot = await getDocs(ordersQuery);
      }
      
      const fetchedOrdersPromises = querySnapshot.docs.map(async (d) => {
        const orderData = d.data();
        const userName = await fetchUserData(orderData.userId);
        // Use Firestore doc ID as id so it's always unique (orderData.id can override and cause duplicates)
        return { ...orderData, id: d.id, userName };
      });
      
      const newOrders = await Promise.all(fetchedOrdersPromises);
      
      // Sort orders manually if we couldn't use orderBy
      if (!newOrders[0]?.packedAt) {
        newOrders.sort((a, b) => {
          const aTime = a.updatedAt || a.createdAt || a.packedAt;
          const bTime = b.updatedAt || b.createdAt || b.packedAt;
          if (!aTime || !bTime) return 0;
          return bTime.toDate() - aTime.toDate();
        });
      }
      
      if (isLoadMore) {
        // Deduplicate: filter out orders already in the list (handles race conditions / double Load More)
        const existingIds = new Set();
        setPackedOrders(prev => {
          prev.forEach(o => existingIds.add(o.id));
          const toAdd = newOrders.filter(o => !existingIds.has(o.id));
          return [...prev, ...toAdd];
        });
      } else {
        setPackedOrders(newOrders);
      }
      
      // Update pagination state
      setHasMore(querySnapshot.docs.length === ordersPerPage);
      if (querySnapshot.docs.length > 0) {
        const newLastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
        setLastDoc(newLastDoc);
        lastDocRef.current = newLastDoc;
      }
      
    } catch (err) {
      setError(`Failed to load packed orders. ${  err.message}`);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setRefreshing(false);
    }
  }, [currentUser, userRole, employeeSupermarketId, ordersPerPage]);

  // Fetch supermarket financial data setting
  useEffect(() => {
    if (userRole === 'Employee' && employeeSupermarketId) {
      const supermarketDocRef = doc(db, "Supermarkets", employeeSupermarketId);
      getDoc(supermarketDocRef).then(docSnap => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setShowFinancialData(data.show_financial_data !== undefined ? data.show_financial_data : true);
        } else {
          setShowFinancialData(true);
        }
      }).catch(_err => {
        setShowFinancialData(true);
      });
    } else if (userRole === 'Admin') {
      // Admins always see financial data
      setShowFinancialData(true);
    }
  }, [userRole, employeeSupermarketId]);

  useEffect(() => {
    fetchPackedOrders();
    fetchSummaryStats();
  }, [fetchPackedOrders, fetchSummaryStats]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      setCurrentPage(prev => prev + 1);
      fetchPackedOrders(true);
    }
  };

  const handleRefresh = () => {
    fetchPackedOrders(false, true);
  };

  const handleUpdateStatus = async (transactionId, newStatus) => {
    setUpdating(transactionId);
    setError(null);
    if (userRole !== 'Admin' && userRole !== 'Employee') {
        setError("You are not authorized to perform this action."); 
        setUpdating(null); 
        return;
    }
    const orderBeingUpdated = packedOrders.find(o => o.id === transactionId);
    if (!orderBeingUpdated) {
      setError(`Order data for ${transactionId} not found locally.`); 
      setUpdating(null); 
      return;
    }
    try {
      const orderRef = doc(db, 'Transactions', transactionId);
      const updatePayload = { status: newStatus, updatedAt: Timestamp.fromDate(new Date()) };
      if (newStatus === 'completed') {
        updatePayload.pickedUpAt = Timestamp.fromDate(new Date());
        updatePayload.completedAt = Timestamp.fromDate(new Date());
      } else if (newStatus === 'cancelled_by_employee') {
        updatePayload.cancelledAt = Timestamp.fromDate(new Date());
        updatePayload.cancellationReason = "Cancelled by employee after packing.";
      }
      await updateDoc(orderRef, updatePayload);
      
      if (newStatus === 'completed' || newStatus === 'cancelled_by_employee') {
        const cartDocId = orderBeingUpdated.cartDocId || orderBeingUpdated.userId;
        if (cartDocId && orderBeingUpdated.supermarketId && orderBeingUpdated.cartInstanceId) {
          try {
            const cartRef = doc(db, 'Carts', cartDocId);
            const cartUpdatePayload = {
              [`${orderBeingUpdated.supermarketId}.${orderBeingUpdated.cartInstanceId}.status`]: newStatus,
              [`${orderBeingUpdated.supermarketId}.${orderBeingUpdated.cartInstanceId}.hasUnseenUpdate`]: true,
              [`${orderBeingUpdated.supermarketId}.${orderBeingUpdated.cartInstanceId}.lastUpdatedAt`]: Timestamp.fromDate(new Date()),
              lastGlobalUpdate: Timestamp.fromDate(new Date())
            };
            await updateDoc(cartRef, cartUpdatePayload);
          } catch (cartError) {
            setError(prev => prev ? `${prev} | Note: Cart instance status update failed.` : "Note: Cart instance status update failed.");
          }
        } else {
            logger.warn(`[ManagePackedOrdersPage] Cannot update cart instance: required IDs missing. TX ID ${transactionId}.`);
        }
      }
      setPackedOrders(prevOrders => prevOrders.filter(order => order.id !== transactionId));
    } catch (err) {
      setError(`Failed to update order ${transactionId}. Error: ${err.message}`);
    } finally {
      setUpdating(null);
    }
  };

  if (loading && packedOrders.length === 0) {
    return (
      <Layout>
        <Container className="mt-4 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading packed orders...</p>
        </Container>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <Container className="mt-4">
        <Row className="align-items-center mb-3">
            <Col xs="auto">
                <Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">
                &larr; Go Back
                </Button>
            </Col>
            <Col>
                <BreadcrumbNavigation items={breadcrumbItems} />
            </Col>
        </Row>
        
        {/* Dashboard Summary Section */}
        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-primary text-white">
            <h5 className="mb-0">
              <i className="fas fa-chart-line me-2"></i>
              Packed Orders Dashboard Summary
            </h5>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              {/* Order Status Cards */}
              <Col xs={12} sm={6} md={3}>
                <Card className="h-100 border-warning">
                  <Card.Body className="text-center">
                    <div className="text-warning mb-2">
                      <i className="fas fa-box-open fa-2x"></i>
                    </div>
                    <h3 className="mb-1">{summaryStats.totalPackedAwaitingPickup}</h3>
                    <p className="text-muted mb-0 small">Awaiting Pickup</p>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col xs={12} sm={6} md={3}>
                <Card className="h-100 border-success">
                  <Card.Body className="text-center">
                    <div className="text-success mb-2">
                      <i className="fas fa-check-circle fa-2x"></i>
                    </div>
                    <h3 className="mb-1">{summaryStats.totalCompleted}</h3>
                    <p className="text-muted mb-0 small">Completed</p>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col xs={12} sm={6} md={3}>
                <Card className="h-100 border-danger">
                  <Card.Body className="text-center">
                    <div className="text-danger mb-2">
                      <i className="fas fa-times-circle fa-2x"></i>
                    </div>
                    <h3 className="mb-1">{summaryStats.totalCancelled}</h3>
                    <p className="text-muted mb-0 small">Cancelled</p>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col xs={12} sm={6} md={3}>
                <Card className="h-100 border-secondary">
                  <Card.Body className="text-center">
                    <div className="text-secondary mb-2">
                      <i className="fas fa-shopping-cart fa-2x"></i>
                    </div>
                    <h3 className="mb-1">{summaryStats.totalOrders}</h3>
                    <p className="text-muted mb-0 small">Total Packed Orders</p>
                  </Card.Body>
                </Card>
              </Col>
              
              {/* Delivery Type Cards */}
              <Col xs={12} sm={6} md={4}>
                <Card className="h-100 border-info">
                  <Card.Body className="text-center">
                    <div className="text-info mb-2">
                      <i className="fas fa-truck fa-2x"></i>
                    </div>
                    <h3 className="mb-1">{summaryStats.deliveryOrders}</h3>
                    <p className="text-muted mb-0 small">Delivery Orders</p>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col xs={12} sm={6} md={4}>
                <Card className="h-100 border-primary">
                  <Card.Body className="text-center">
                    <div className="text-primary mb-2">
                      <i className="fas fa-store fa-2x"></i>
                    </div>
                    <h3 className="mb-1">{summaryStats.pickupOrders}</h3>
                    <p className="text-muted mb-0 small">Pickup Orders</p>
                  </Card.Body>
                </Card>
              </Col>
              
              {/* Payment Type Cards - Only shown if showFinancialData is true */}
              {showFinancialData && (
                <>
                  <Col xs={12} sm={6} md={4}>
                    <Card className="h-100 border-success">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted small">Full Payment Orders</span>
                          <Badge bg="success">{summaryStats.fullPaymentCount}</Badge>
                        </div>
                        <div className="text-success">
                          <strong>${Number(summaryStats.fullPaymentTotal).toFixed(2)}</strong>
                        </div>
                        <div className="small text-muted mt-1">
                          Subtotal: ${Number(summaryStats.fullPaymentMinusConfirmationFee).toFixed(2)}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  
                  <Col xs={12} sm={6} md={4}>
                    <Card className="h-100 border-secondary">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted small">Confirmation Fee Only</span>
                          <Badge bg="secondary">{summaryStats.confirmationFeeOnlyCount}</Badge>
                        </div>
                        <div className="text-dark">
                          <strong>${Number(summaryStats.confirmationFeeTotal).toFixed(2)}</strong>
                        </div>
                        <div className="small text-muted mt-1">
                          Fees collected
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  
                  <Col xs={12} sm={6} md={4}>
                    <Card className="h-100 border-warning">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted small">Total Revenue</span>
                          <i className="fas fa-dollar-sign text-warning"></i>
                        </div>
                        <div className="text-warning">
                          <strong>${(Number(summaryStats.fullPaymentTotal) + Number(summaryStats.confirmationFeeTotal)).toFixed(2)}</strong>
                        </div>
                        <div className="small text-muted mt-1">
                          All payments collected
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </>
              )}
            </Row>
          </Card.Body>
        </Card>

        <Card className="shadow-sm">
          <Card.Header className="bg-light border-bottom d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-2 py-3">
            <h2 className="mb-0 text-break text-dark fw-bold fs-5">
              <i className="fas fa-boxes me-2 text-primary"></i>
              Manage Packed Orders 
              {userRole === 'Admin' ? ' (Admin View - All Supermarkets)' : 
               (employeeSupermarketId ? '' : ' (No Supermarket Assigned)')}
            </h2>
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex-shrink-0"
            >
              {refreshing ? (
                <>
                  <Spinner as="span" size="sm" className="me-2" />
                  Refreshing...
                </>
              ) : (
                <>
                  <i className="fas fa-sync-alt me-1"></i>
                  Refresh
                </>
              )}
            </Button>
          </Card.Header>
          <Card.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            {refreshing && (
              <Alert variant="info">
                <Spinner as="span" size="sm" className="me-2" />
                Refreshing orders...
              </Alert>
            )}

            {packedOrders.length === 0 && !loading && (
              <Alert variant="info">
                <i className="fas fa-info-circle me-2"></i>
                No orders are currently packed and awaiting pickup or confirmed 
                {userRole === 'Employee' && employeeSupermarketId ? ` for your assigned supermarket` : 
                 (userRole === 'Employee' ? ' (No supermarket assigned)':'.')}.
              </Alert>
            )}

            {packedOrders.length > 0 && (
              <div>
                <div className="mb-3 d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
                  <div className="d-flex flex-wrap gap-2">
                    <Badge bg="secondary">
                      <i className="fas fa-list me-1"></i>
                      {packedOrders.length} Order{packedOrders.length !== 1 ? 's' : ''}
                    </Badge>
                    <Badge bg="info">
                      <i className="fas fa-clock me-1"></i>
                      Page {currentPage}
                    </Badge>
                  </div>
                  <small className="text-muted text-nowrap">
                    <i className="fas fa-info-circle me-1"></i>
                    Last updated: {formatDateTime(new Date())}
                  </small>
                </div>
                
                <div className="row g-3">
                  {packedOrders.map((order, index) => {
                    const confirmationInfo = getConfirmationInfo(order);
                    
                    return (
                      <div key={`${order.id}-${index}`} className="col-12 col-lg-6 col-xl-4">
                        <Card className={`shadow-sm h-100 ${order.status === 'completed' ? 'border-success' : 'border-warning'}`}>
                          <Card.Body className="p-3">
                            {/* Order Header */}
                            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center mb-2">
                              <div className="flex-grow-1">
                                <h6 className="mb-1 text-break text-primary small">
                                  <i className="fas fa-receipt me-1"></i>
                                  {order.id}
                                </h6>
                                <small className="text-muted text-break">
                                  <i className="fas fa-shopping-cart me-1"></i>
                                  Cart: {order.cartInstanceId ? order.cartInstanceId.replace('cart_instance_', '') : 'N/A'}
                                </small>
                              </div>
                              <div className="text-end mt-1 mt-lg-0">
                                <Badge bg="success" className="small">
                                  <i className="fas fa-box me-1"></i>
                                  {formatDateTime(order.packedAt)}
                                </Badge>
                              </div>
                            </div>
                            
                            {/* Payment Type Indicator */}
                            {isFullPaymentOrder(order) && (
                              <div className="mb-2">
                                <Badge bg="success" className="small">
                                  <i className="fas fa-check-circle me-1"></i>
                                  Full Payment - Paid in Full
                                </Badge>
                              </div>
                            )}
                            
                            {/* Order Details */}
                            <div className="mb-2">
                              <div className="d-flex align-items-center mb-1">
                                <i className="fas fa-store me-1 text-primary small"></i>
                                <strong className="small">Supermarket:</strong>
                              </div>
                              <p className="mb-1 small text-break">{order.supermarketName} <small className="text-muted">(ID: {order.supermarketId})</small></p>
                              
                              <div className="d-flex align-items-center mb-1">
                                <i className="fas fa-user me-1 text-primary small"></i>
                                <strong className="small">Customer:</strong>
                              </div>
                              <p className="mb-1 small">{order.userName}</p>
                              
                              {/* Delivery Information */}
                              {order.deliveryOption === 'delivery' && (
                                <div className="mb-1">
                                  <Badge bg="info" className="small mb-1">
                                    <i className="fas fa-truck me-1"></i>
                                    Delivery
                                  </Badge>
                                  {order.deliveryAddress && (
                                    <div className="mb-1">
                                      <strong className="small"><i className="fas fa-map-marker-alt me-1 text-primary"></i>Address:</strong>
                                      <p className="mb-0 small text-break">{order.deliveryAddress}</p>
                                    </div>
                                  )}
                                  {order.deliveryPhone && (
                                    <div className="mb-1">
                                      <strong className="small"><i className="fas fa-phone me-1 text-primary"></i>Phone:</strong>
                                      <p className="mb-0 small text-break">{order.deliveryPhone}</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="d-flex flex-column gap-1">
                              <Link to={`/order-status/${order.id}`} className="btn btn-outline-info btn-sm">
                                <i className="fas fa-eye me-1"></i>
                                View Details
                              </Link>
                              
                              {/* Show different buttons based on order status */}
                              {order.status === 'packed_awaiting_pickup' ? (
                                <div className="d-flex flex-column flex-sm-row gap-1">
                                  <Button 
                                    variant="success" 
                                    size="sm" 
                                    onClick={() => handleUpdateStatus(order.id, 'completed')} 
                                    disabled={updating === order.id}
                                    className="flex-grow-1"
                                  >
                                    {updating === order.id ? (
                                      <>
                                        <Spinner as="span" size="sm" className="me-1"/> 
                                        Updating...
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-check me-1"></i>
                                        {order.deliveryOption === 'delivery' ? 'Confirm Delivery' : 'Confirm Pickup'}
                                      </>
                                    )} 
                                  </Button>
                                  <Button 
                                    variant="warning" 
                                    size="sm" 
                                    onClick={() => handleUpdateStatus(order.id, 'cancelled_by_employee')} 
                                    disabled={updating === order.id}
                                    className="flex-grow-1"
                                  >
                                    {updating === order.id ? (
                                      <>
                                        <Spinner as="span" size="sm" className="me-1"/> 
                                        Updating...
                                      </>
                                    ) : (
                                      <>
                                        <i className="fas fa-times me-1"></i>
                                        Cancel
                                      </>
                                    )}
                                  </Button>
                                </div>
                              ) : order.status === 'completed' && confirmationInfo ? (
                                <div className="d-flex flex-column gap-1">
                                  <Badge 
                                    bg={confirmationInfo.type === 'employee' ? 'success' : 'info'} 
                                    className="p-1 text-center small"
                                  >
                                    <i className={`fas ${confirmationInfo.type === 'employee' ? 'fa-user-tie' : 'fa-user'} me-1`}></i>
                                    {confirmationInfo.text}
                                  </Badge>
                                  {confirmationInfo.timestamp && (
                                    <small className="text-muted text-center">
                                      <i className="fas fa-clock me-1"></i>
                                      {formatDateTime(confirmationInfo.timestamp)}
                                    </small>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    );
                  })}
                </div>
                
                {/* Load More Button */}
                {hasMore && (
                  <div className="text-center mt-4">
                    <Button 
                      variant="outline-primary" 
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      size="lg"
                      className="w-100 w-sm-auto"
                    >
                      {loadingMore ? (
                        <>
                          <Spinner as="span" size="sm" className="me-2" />
                          Loading More Orders...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plus me-2"></i>
                          Load More Orders
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                {/* End of orders indicator */}
                {!hasMore && packedOrders.length > 0 && (
                  <div className="text-center mt-4">
                    <Alert variant="light">
                      <i className="fas fa-check-circle text-success me-2"></i>
                      All orders have been loaded
                    </Alert>
                  </div>
                )}
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </Layout>
  );
}
